/**
 * Summani so'z bilan yozish — to'lov cheki (kvitansiya) uchun.
 * Raqam yonida so'z bilan takrorlanishi chekni qalbakilashtirishdan himoya qiladi.
 * Faqat butun musbat qiymatlar (so'mda tiyin ishlatilmaydi).
 */

/* ─── O'zbekcha ─── */
const UZ_ONES = ['', 'bir', 'ikki', 'uch', "to'rt", 'besh', 'olti', 'yetti', 'sakkiz', "to'qqiz"];
const UZ_TENS = ['', "o'n", 'yigirma', "o'ttiz", 'qirq', 'ellik', 'oltmish', 'yetmish', 'sakson', "to'qson"];
const UZ_GROUPS = ['', 'ming', 'million', 'milliard', 'trillion'];

function uzTriad(n: number): string[] {
  const out: string[] = [];
  const h = Math.floor(n / 100);
  const t = Math.floor((n % 100) / 10);
  const o = n % 10;
  if (h) out.push(UZ_ONES[h], 'yuz');
  if (t) out.push(UZ_TENS[t]);
  if (o) out.push(UZ_ONES[o]);
  return out;
}

/* ─── Ruscha ─── */
const RU_ONES_M = ['', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять'];
const RU_ONES_F = ['', 'одна', 'две', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять'];
const RU_TEENS = ['десять', 'одиннадцать', 'двенадцать', 'тринадцать', 'четырнадцать', 'пятнадцать', 'шестнадцать', 'семнадцать', 'восемнадцать', 'девятнадцать'];
const RU_TENS = ['', '', 'двадцать', 'тридцать', 'сорок', 'пятьдесят', 'шестьдесят', 'семьдесят', 'восемьдесят', 'девяносто'];
const RU_HUNDREDS = ['', 'сто', 'двести', 'триста', 'четыреста', 'пятьсот', 'шестьсот', 'семьсот', 'восемьсот', 'девятьсот'];
const RU_GROUPS: [string, string, string][] = [
  ['', '', ''],
  ['тысяча', 'тысячи', 'тысяч'],
  ['миллион', 'миллиона', 'миллионов'],
  ['миллиард', 'миллиарда', 'миллиардов'],
  ['триллион', 'триллиона', 'триллионов'],
];

/** Ruscha son-son shakli: 1 тысяча / 2 тысячи / 5 тысяч. */
function ruPlural(n: number, forms: [string, string, string]): string {
  const n10 = n % 10;
  const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return forms[0];
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return forms[1];
  return forms[2];
}

/** `fem` — "тысяча" ayol rodida (одна тысяча, две тысячи). */
function ruTriad(n: number, fem: boolean): string[] {
  const out: string[] = [];
  const h = Math.floor(n / 100);
  const rest = n % 100;
  if (h) out.push(RU_HUNDREDS[h]);
  if (rest >= 10 && rest < 20) {
    out.push(RU_TEENS[rest - 10]);
  } else {
    const t = Math.floor(rest / 10);
    const o = rest % 10;
    if (t) out.push(RU_TENS[t]);
    if (o) out.push((fem ? RU_ONES_F : RU_ONES_M)[o]);
  }
  return out;
}

/* ─── Inglizcha ─── */
const EN_ONES = [
  '', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
  'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen',
];
const EN_TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
const EN_GROUPS = ['', 'thousand', 'million', 'billion', 'trillion'];

function enTriad(n: number): string[] {
  const out: string[] = [];
  const h = Math.floor(n / 100);
  const rest = n % 100;
  if (h) out.push(EN_ONES[h], 'hundred');
  if (rest < 20) {
    if (rest) out.push(EN_ONES[rest]);
  } else {
    const t = Math.floor(rest / 10);
    const o = rest % 10;
    out.push(EN_TENS[t] + (o ? `-${EN_ONES[o]}` : ''));
  }
  return out;
}

/**
 * Summani joriy tilda so'z bilan qaytaradi (birinchi harf katta), valyutasiz.
 * Masalan (uz): 1 200 000 → "Bir million ikki yuz ming".
 * Juda katta (10^15 dan ortiq) sonlar so'zga aylantirilmaydi — raqamning o'zi qaytadi.
 */
export function amountInWords(amount: number | null | undefined, lang = 'uz'): string {
  const n = Math.floor(Math.abs(Number(amount) || 0));
  const l = (lang || 'uz').slice(0, 2);
  if (n === 0) return l === 'ru' ? 'Ноль' : l === 'en' ? 'Zero' : 'Nol';

  // Sonni 3 xonali guruhlarga (triadalarga) ajratamiz: [birlik, ming, million, ...]
  const triads: number[] = [];
  for (let rest = n; rest > 0; rest = Math.floor(rest / 1000)) triads.push(rest % 1000);
  if (triads.length > EN_GROUPS.length) return String(n);

  const words: string[] = [];
  for (let i = triads.length - 1; i >= 0; i--) {
    const v = triads[i];
    if (!v) continue;
    if (l === 'ru') {
      words.push(...ruTriad(v, i === 1)); // ming guruhi ayol rodida
      if (i > 0) words.push(ruPlural(v, RU_GROUPS[i]));
    } else if (l === 'en') {
      words.push(...enTriad(v));
      if (i > 0) words.push(EN_GROUPS[i]);
    } else {
      words.push(...uzTriad(v));
      if (i > 0) words.push(UZ_GROUPS[i]);
    }
  }

  const s = words.join(' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}
